import { useState, useEffect } from "react";
import { fetchSession, type AuthSessionInfo } from "../../api/auth";
import { fetchTenantMembers, inviteMember, removeMember, type TenantMember } from "../../api/tenants";
import "./UserManagementPage.css";

export function UserManagementPage() {
  const [session, setSession] = useState<AuthSessionInfo | null>(null);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRoles, setInviteRoles] = useState<string[]>(["member"]);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const sessionData = await fetchSession();
      setSession(sessionData);

      if (sessionData.authenticated && sessionData.user) {
        const membersData = await fetchTenantMembers(sessionData.user.tenantId);
        setMembers(membersData);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(err instanceof Error ? err.message : "Fehler beim Laden der Daten");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session?.user) return;

    try {
      setIsInviting(true);
      setInviteError(null);

      await inviteMember(session.user.tenantId, {
        email: inviteEmail,
        roles: inviteRoles
      });

      // Erfolgreich - Dialog schließen und Daten neu laden
      setShowInviteDialog(false);
      setInviteEmail("");
      setInviteRoles(["member"]);
      await loadData();
    } catch (err) {
      console.error("Failed to invite member:", err);
      setInviteError(err instanceof Error ? err.message : "Fehler beim Einladen");
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!session?.user) return;
    
    if (!confirm("Möchten Sie dieses Mitglied wirklich entfernen?")) {
      return;
    }

    try {
      await removeMember(session.user.tenantId, userId);
      await loadData();
    } catch (err) {
      console.error("Failed to remove member:", err);
      alert(err instanceof Error ? err.message : "Fehler beim Entfernen");
    }
  };

  const toggleRole = (role: string) => {
    if (inviteRoles.includes(role)) {
      setInviteRoles(inviteRoles.filter(r => r !== role));
    } else {
      setInviteRoles([...inviteRoles, role]);
    }
  };

  const canManageUsers = session?.user?.roles.includes("owner") || session?.user?.roles.includes("admin");

  if (isLoading) {
    return (
      <div className="user-management-container">
        <div className="user-management-loading">Lade Benutzerdaten...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management-container">
        <div className="user-management-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      <header className="user-management-header">
        <div>
          <h1 className="user-management-title">Benutzerverwaltung</h1>
          <p className="user-management-subtitle">
            {members.length} {members.length === 1 ? "Mitglied" : "Mitglieder"}
          </p>
        </div>
        {canManageUsers && (
          <button
            className="user-management-invite-button"
            onClick={() => setShowInviteDialog(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
            Benutzer einladen
          </button>
        )}
      </header>

      <div className="user-management-list">
        {members.map((member) => (
          <div key={member.id} className="user-management-card">
            <div className="user-management-card-avatar">
              {member.displayName.substring(0, 2).toUpperCase()}
            </div>
            <div className="user-management-card-content">
              <div className="user-management-card-name">{member.displayName}</div>
              <div className="user-management-card-email">{member.email}</div>
              <div className="user-management-card-roles">
                {member.roles.map(role => (
                  <span key={role} className="user-management-role-badge">{role}</span>
                ))}
                {member.isPrimary && (
                  <span className="user-management-primary-badge">Primärer Workspace</span>
                )}
              </div>
              {member.joinedAt && (
                <div className="user-management-card-joined">
                  Beigetreten: {new Date(member.joinedAt).toLocaleDateString("de-DE")}
                </div>
              )}
            </div>
            {canManageUsers && !member.isPrimary && (
              <button
                className="user-management-remove-button"
                onClick={() => handleRemoveMember(member.id)}
                title="Mitglied entfernen"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            )}
          </div>
        ))}

        {members.length === 0 && (
          <div className="user-management-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <p>Keine Mitglieder gefunden</p>
          </div>
        )}
      </div>

      {/* Invite Dialog */}
      {showInviteDialog && (
        <div className="user-management-dialog-overlay" onClick={() => setShowInviteDialog(false)}>
          <div className="user-management-dialog" onClick={(e) => e.stopPropagation()}>
            <header className="user-management-dialog-header">
              <h2>Benutzer einladen</h2>
              <button
                className="user-management-dialog-close"
                onClick={() => setShowInviteDialog(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </header>
            
            <form onSubmit={handleInvite} className="user-management-dialog-form">
              {inviteError && (
                <div className="user-management-dialog-error">{inviteError}</div>
              )}

              <div className="user-management-dialog-field">
                <label htmlFor="invite-email">E-Mail-Adresse</label>
                <input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="benutzer@beispiel.de"
                  required
                />
              </div>

              <div className="user-management-dialog-field">
                <label>Rollen</label>
                <div className="user-management-roles-grid">
                  {["member", "admin", "owner"].map(role => (
                    <label key={role} className="user-management-role-checkbox">
                      <input
                        type="checkbox"
                        checked={inviteRoles.includes(role)}
                        onChange={() => toggleRole(role)}
                      />
                      <span>{role}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="user-management-dialog-actions">
                <button
                  type="button"
                  className="user-management-dialog-cancel"
                  onClick={() => setShowInviteDialog(false)}
                  disabled={isInviting}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="user-management-dialog-submit"
                  disabled={isInviting || inviteRoles.length === 0}
                >
                  {isInviting ? "Lädt ein..." : "Einladen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
